import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { foundationAPI, jobApiError } from '../services/api';
import { FiAlertCircle, FiCheckCircle, FiDownload, FiPlus, FiTrash2 } from 'react-icons/fi';
import MarkdownView from './MarkdownView';

// The Foundation document, master copy in the VMS (decision Q14,
// brainstorms/2026-10-17-foundation-enforcement.md). Part 1 is the team rules (own tab);
// parts 2-8 are versioned here. Part 2 holds the RACI chart as data: publishing it makes
// that chart the one tasks and complaints use to find who is Accountable.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const muted = { color: '#64748b', fontSize: 12 };
const rowStyle = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);
const Ok = ({ text }) => (text ? <div style={{ color: '#15803d', fontSize: 13, margin: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}><FiCheckCircle />{text}</div> : null);
const LETTERS = ['', 'R', 'A', 'A/R', 'C', 'I'];
const SECTION_ORDER = ['A', 'B', 'C', 'E', 'D'];

const download = async (drafts) => {
  const res = await foundationAPI.exportDocument(drafts);
  const url = URL.createObjectURL(new Blob([res.data], { type: 'text/markdown' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = drafts ? 'VisionExploit_Foundation_review_copy.md' : 'VisionExploit_Foundation.md';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// ─── RACI tables ───────────────────────────────────────────────────────────
const letterColour = (l) => (l === 'A' || l === 'A/R' ? '#0B1760' : l === 'R' ? '#011ff3' : '#64748b');

export const RaciSection = ({ rows, roles, section, editable, onChange }) => {
  const mine = rows.filter((r) => r.section === section);
  const setCell = (code, role, letter) => onChange(rows.map((r) => {
    if (r.code !== code) return r;
    const cells = { ...r.cells };
    if (letter) cells[role] = letter; else delete cells[role];
    return { ...r, cells };
  }));
  const setTask = (code, task) => onChange(rows.map((r) => (r.code === code ? { ...r, task } : r)));
  const remove = (code) => window.confirm(`Remove RACI row ${code}?`) && onChange(rows.filter((r) => r.code !== code));
  let group = null;
  return (
    <div style={{ overflowX: 'auto', margin: '6px 0 14px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: 6, background: '#f1f5f9', width: 46 }}>#</th>
            <th style={{ textAlign: 'left', padding: 6, background: '#f1f5f9' }}>Task</th>
            {roles.map((r) => <th key={r.code} style={{ padding: 6, background: '#f1f5f9', width: 54 }} title={r.name}>{r.code}</th>)}
            {editable && <th style={{ background: '#f1f5f9', width: 30 }} />}
          </tr>
        </thead>
        <tbody>
          {mine.map((r) => {
            const header = r.group && r.group !== group ? (group = r.group) : null;
            const owners = Object.values(r.cells).filter((l) => l === 'A' || l === 'A/R').length;
            const doers = Object.values(r.cells).filter((l) => l === 'R' || l === 'A/R').length;
            const bad = owners !== 1 || doers < 1;
            return (
              <React.Fragment key={r.code}>
                {header && <tr><td colSpan={roles.length + 3} style={{ padding: '8px 6px 4px', fontWeight: 600, color: '#0B1760' }}>{header}</td></tr>}
                <tr style={{ background: bad ? '#fef2f2' : undefined }}>
                  <td style={{ padding: 6, borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>{r.code}</td>
                  <td style={{ padding: 6, borderBottom: '1px solid #e2e8f0' }}>
                    {editable ? <input style={{ width: '100%', fontSize: 13 }} value={r.task} onChange={(e) => setTask(r.code, e.target.value)} /> : r.task}
                    {bad && <div style={{ color: '#b91c1c', fontSize: 11 }}>Needs exactly one A (or A/R) and at least one R.</div>}
                  </td>
                  {roles.map((role) => (
                    <td key={role.code} style={{ padding: 4, borderBottom: '1px solid #e2e8f0', textAlign: 'center', fontWeight: 600, color: letterColour(r.cells[role.code]) }}>
                      {editable ? (
                        <select value={r.cells[role.code] || ''} onChange={(e) => setCell(r.code, role.code, e.target.value)} style={{ width: 52, padding: 2, fontSize: 12 }}>
                          {LETTERS.map((l) => <option key={l} value={l}>{l || '·'}</option>)}
                        </select>
                      ) : (r.cells[role.code] || '')}
                    </td>
                  ))}
                  {editable && <td style={{ borderBottom: '1px solid #e2e8f0' }}><button type="button" className="vc-icon-btn" onClick={() => remove(r.code)} aria-label={`Remove ${r.code}`}><FiTrash2 size={14} /></button></td>}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AddRaciRow = ({ rows, onChange }) => {
  const [f, setF] = useState({ section: 'A', code: '', task: '', group: '' });
  const sections = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.section] = r.section_title; });
    return m;
  }, [rows]);
  const add = () => {
    if (rows.some((r) => r.code.toUpperCase() === f.code.trim().toUpperCase())) { window.alert('That code is already used.'); return; }
    const row = { code: f.code.trim(), section: f.section, section_title: sections[f.section] || f.section, group: f.group.trim(), task: f.task.trim(), cells: {} };
    const last = rows.map((r) => r.section).lastIndexOf(f.section);
    const next = [...rows];
    next.splice(last + 1, 0, row);
    onChange(next);
    setF({ ...f, code: '', task: '' });
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 14px' }}>
      <select value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })}>
        {SECTION_ORDER.map((s) => <option key={s} value={s}>{sections[s] || s}</option>)}
      </select>
      <input style={{ width: 70 }} placeholder="Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
      <input style={{ flex: 1, minWidth: 200 }} placeholder="Task" value={f.task} onChange={(e) => setF({ ...f, task: e.target.value })} />
      <input style={{ width: 160 }} placeholder="Group (optional)" value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} />
      <button type="button" className="btn-secondary" disabled={!f.code.trim() || !f.task.trim()} onClick={add}><FiPlus /> Add row</button>
    </div>
  );
};

// ─── One version: read, sign, edit, publish ────────────────────────────────
const VersionPanel = ({ id, roles, liveRaci, heldReviews, canSeeStatus, onChanged }) => {
  const [v, setV] = useState(null);
  const [edit, setEdit] = useState(null);
  const [status, setStatus] = useState(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [review, setReview] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await foundationAPI.partVersion(id);
      setV(r.data);
      setEdit(r.data.can_edit ? { body: r.data.body, changes_summary: r.data.changes_summary, raci: r.data.raci } : null);
      setStatus(r.data.status !== 'DRAFT' && canSeeStatus ? (await foundationAPI.partStatus(id)).data : null);
      setErr('');
    } catch (e) { setErr(jobApiError(e)); }
  }, [id, canSeeStatus]);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, done) => { try { await fn(); setOk(done); setErr(''); await load(); onChanged(); } catch (e) { setOk(''); setErr(jobApiError(e)); } };
  if (!v) return <Err text={err} />;

  const raciRows = (edit && edit.raci) || v.raci || liveRaci;
  const blocks = {};
  SECTION_ORDER.forEach((s) => { blocks[s] = <RaciSection rows={raciRows} roles={roles} section={s} editable={false} />; });
  const save = (extra) => foundationAPI.updatePartVersion(v.id, { body: edit.body, changes_summary: edit.changes_summary, ...(v.part === 2 ? { raci: edit.raci } : {}), ...extra });

  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title">
        <span>Part {v.part} · v{v.version} · {v.status_label}</span>
        <span>{v.published_at ? `in force since ${fmtDate(v.published_at)}${v.initial_adoption ? ' (first version in the VMS)' : v.review ? ` · agreed at the ${v.review} review` : ''}` : 'not published'}</span>
      </div>
      <Err text={err} /><Ok text={ok} />
      {v.changes_summary && <div style={{ ...muted, marginBottom: 10 }}>What changed: {v.changes_summary}</div>}

      {edit ? (
        <>
          <label style={muted}>What changed (shown to everyone)</label>
          <textarea rows={3} style={{ width: '100%' }} value={edit.changes_summary} onChange={(e) => setEdit({ ...edit, changes_summary: e.target.value })} />
          <label style={muted}>Text (markdown: ## headings, - lists, | tables |, **bold**{v.part === 2 ? '; {{raci:A}} … {{raci:E}} show the RACI sections' : ''})</label>
          <textarea rows={20} style={{ width: '100%', fontSize: 13, lineHeight: 1.5, fontFamily: 'monospace' }} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
          {v.part === 2 && edit.raci && (
            <div style={{ marginTop: 12 }}>
              <div className="cv-subsection-title"><span>RACI chart in this draft</span><span>{edit.raci.length} rows</span></div>
              {SECTION_ORDER.map((s) => (
                <div key={s}>
                  <div style={{ fontWeight: 600, margin: '10px 0 2px' }}>{(edit.raci.find((r) => r.section === s) || {}).section_title || s}</div>
                  <RaciSection rows={edit.raci} roles={roles} section={s} editable onChange={(raci) => setEdit({ ...edit, raci })} />
                </div>
              ))}
              <AddRaciRow rows={edit.raci} onChange={(raci) => setEdit({ ...edit, raci })} />
            </div>
          )}
          <details style={{ margin: '10px 0' }}>
            <summary style={{ cursor: 'pointer', ...muted }}>Preview</summary>
            <MarkdownView text={edit.body} blocks={blocks} />
          </details>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', marginTop: 8 }}>
            {heldReviews.length > 0 && (
              <select value={review} onChange={(e) => setReview(e.target.value)} title="Needed for every version after the first">
                <option value="">Agreed at review… (not needed the first time)</option>
                {heldReviews.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            )}
            <button className="btn-secondary" onClick={() => run(() => save(), 'Draft saved.')}>Save draft</button>
            <button className="btn-primary" onClick={() => window.confirm(`Put Part ${v.part} v${v.version} in force? The team will be asked to acknowledge it within 7 days.${v.part === 2 ? ' Its RACI chart replaces the one in force.' : ''}`)
              && run(async () => { await save(); await foundationAPI.publishPartVersion(v.id, review ? { review: Number(review) } : {}); }, 'In force. The team has been notified.')}>Publish</button>
          </div>
        </>
      ) : (
        <div style={{ maxHeight: 640, overflowY: 'auto', paddingRight: 4 }}><MarkdownView text={v.body} blocks={blocks} /></div>
      )}

      {v.status === 'PUBLISHED' && (
        <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 12, paddingTop: 12 }}>
          {v.my_acknowledgement ? (
            <Ok text={`You acknowledged this version on ${fmtDate(v.my_acknowledgement.acknowledged_at)} as "${v.my_acknowledgement.typed_name}".`} />
          ) : (
            <>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, marginBottom: 8 }}>
                <input type="checkbox" style={{ width: 'auto', marginTop: 3 }} checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                I have read and understood Part {v.part} of the VisionExploit Foundation.
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input style={{ flex: 1, minWidth: 220 }} placeholder="Type your full name" value={name} onChange={(e) => setName(e.target.value)} />
                <button className="btn-primary" disabled={!agree || name.trim().split(/\s+/).length < 2}
                  onClick={() => run(() => foundationAPI.acknowledgePart(v.id, { typed_name: name, agree }), 'Acknowledged.')}>Acknowledge</button>
              </div>
              <div style={{ ...muted, marginTop: 6 }}>This replaces the paper signature. The date, time, version and your login are recorded with it.</div>
            </>
          )}
        </div>
      )}

      {status && (
        <div style={{ marginTop: 12 }}>
          <div className="cv-subsection-title"><span>Acknowledged: {status.done} of {status.total}</span></div>
          {status.people.map((p) => (
            <div key={p.user_id} style={{ ...rowStyle, display: 'flex', gap: 8 }}>
              <span style={{ flex: 1 }}>{p.name} <span style={muted}>· {p.role}</span></span>
              {p.acknowledged_at ? <span style={{ color: '#15803d' }}>{fmtDate(p.acknowledged_at)} · “{p.typed_name}”</span> : <span style={{ color: '#b45309' }}>Not yet</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── One part: its versions ────────────────────────────────────────────────
const PartPanel = ({ number, roles, liveRaci, heldReviews, canSeeStatus, onChanged }) => {
  const [part, setPart] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => foundationAPI.part(number).then((r) => {
    setPart(r.data);
    setOpenId((cur) => cur || (r.data.versions.find((v) => v.status === 'PUBLISHED') || r.data.versions[0] || {}).id || null);
  }).catch((e) => setErr(jobApiError(e))), [number]);
  useEffect(() => { setOpenId(null); load(); }, [load]);
  if (!part) return <Err text={err} />;
  const hasDraft = part.versions.some((v) => v.status === 'DRAFT');
  const changed = () => { load(); onChanged(); };
  return (
    <>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Part {part.number}: {part.title}</span><span>Owner: {part.owner.name}</span></div>
        <Err text={err} />
        {part.versions.length === 0 && <div className="cv-subsection-empty">No version of this part is in the VMS yet.</div>}
        {part.versions.map((v) => (
          <div key={v.id} style={{ ...rowStyle, display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', background: v.id === openId ? '#f8fafc' : undefined }} onClick={() => setOpenId(v.id)}>
            <strong style={{ minWidth: 60 }}>v{v.version}</strong>
            <span className="status-pill" style={{ color: v.status === 'PUBLISHED' ? '#15803d' : v.status === 'DRAFT' ? '#b45309' : '#64748b' }}>{v.status_label}</span>
            <span style={{ flex: 1, ...muted }}>{v.published_at ? `published ${fmtDate(v.published_at)} by ${v.published_by}` : `created ${fmtDate(v.created_at)}`}</span>
          </div>
        ))}
        {part.can_edit && !hasDraft && part.versions.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input style={{ width: 120 }} placeholder="e.g. 1.2" value={newVersion} onChange={(e) => setNewVersion(e.target.value)} />
            <button className="btn-secondary" disabled={!newVersion.trim()}
              onClick={() => foundationAPI.newPartDraft(number, { version: newVersion.trim() }).then((r) => { setNewVersion(''); setOpenId(r.data.id); changed(); }).catch((e) => setErr(jobApiError(e)))}>New draft</button>
            <span style={muted}>New versions are published by the owner after a quarterly review.</span>
          </div>
        )}
      </div>
      {openId && <VersionPanel key={openId} id={openId} roles={roles} liveRaci={liveRaci} heldReviews={heldReviews} canSeeStatus={canSeeStatus} onChanged={changed} />}
    </>
  );
};

// ─── The tab ───────────────────────────────────────────────────────────────
const FoundationDocument = ({ isLeader, onOpenRules }) => {
  const [doc, setDoc] = useState(null);
  const [raci, setRaci] = useState(null);
  const [open, setOpen] = useState(null);
  const [lookup, setLookup] = useState('');
  const [found, setFound] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => Promise.all([foundationAPI.document(), foundationAPI.raci()])
    .then(([d, r]) => { setDoc(d.data); setRaci(r.data); setErr(''); window.dispatchEvent(new Event('vec-rules-changed')); })
    .catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!doc || !raci) return <Err text={err} />;

  const find = () => foundationAPI.raciRow(lookup.trim()).then((r) => setFound(r.data)).catch((e) => setFound({ error: jobApiError(e) }));
  const blocks = {};
  SECTION_ORDER.forEach((s) => { blocks[s] = <RaciSection rows={raci.rows} roles={raci.roles} section={s} editable={false} />; });

  return (
    <>
      <div className="cv-subsection">
        <div className="cv-subsection-title">
          <span>The VisionExploit Foundation</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="btn-secondary" onClick={() => download(false).catch((e) => setErr(jobApiError(e)))}><FiDownload /> Download (in force)</button>
            {isLeader && <button className="btn-secondary" onClick={() => download(true).catch((e) => setErr(jobApiError(e)))}><FiDownload /> Review copy (with drafts)</button>}
          </span>
        </div>
        <Err text={err} />
        {doc.outstanding.length > 0 && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>
            Please read and acknowledge: {doc.outstanding.map((o) => `Part ${o.part} v${o.version}`).join(', ')}.
          </div>
        )}
        {doc.parts.map((p) => (
          <div key={p.number} style={{ ...rowStyle, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', background: open === p.number ? '#f8fafc' : undefined }}
            onClick={() => (p.is_rules ? onOpenRules() : setOpen(p.number))}>
            <strong style={{ minWidth: 54 }}>Part {p.number}</strong>
            <span style={{ flex: 1 }}>{p.title} <span style={muted}>· owner {p.owner.code}</span></span>
            <span style={{ ...muted, minWidth: 120 }}>{p.current ? `v${p.current.version} in force` : 'not in force yet'}</span>
            {p.draft && <span className="status-pill" style={{ color: '#b45309' }}>draft v{p.draft.version}</span>}
            {p.current && (p.acknowledged ? <span style={{ color: '#15803d', fontSize: 12 }}>signed</span> : <span style={{ color: '#b45309', fontSize: 12 }}>to sign</span>)}
          </div>
        ))}
        <div style={{ ...muted, marginTop: 8 }}>Part 1 opens the Team rules tab. A part's first version in the VMS is put in force by the Chairman or CEO; after that only its owner publishes, after a quarterly review.</div>
      </div>

      {open && <PartPanel number={open} roles={raci.roles} liveRaci={raci.rows} heldReviews={doc.held_reviews} canSeeStatus={isLeader} onChanged={load} />}

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>RACI chart in force</span><span>{raci.source}</span></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          <input style={{ width: 120 }} placeholder="Row, e.g. A14" value={lookup} onChange={(e) => setLookup(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && find()} />
          <button className="btn-secondary" disabled={!lookup.trim()} onClick={find}>Who is accountable?</button>
          {found && (found.error ? <span style={{ color: '#b91c1c', fontSize: 13 }}>{found.error}</span> : (
            <span style={{ fontSize: 13 }}>
              <strong>{found.code}</strong>: Accountable {found.accountable.role_name}{found.accountable.people.length ? ` (${found.accountable.people.map((x) => x.name).join(', ')})` : ' (nobody holds it yet)'}
              {found.responsible.roles.length > 0 && <> · Responsible {found.responsible.roles.join(', ')}</>}
            </span>
          ))}
        </div>
        <div style={{ ...muted, marginBottom: 6 }}>
          {raci.roles.map((r) => `${r.code} = ${r.name}${r.holders.length ? ` (${r.holders.join(', ')})` : ''}`).join(' · ')}
        </div>
        <MarkdownView text={SECTION_ORDER.map((s) => `### ${(raci.rows.find((r) => r.section === s) || {}).section_title || s}\n{{raci:${s}}}`).join('\n')} blocks={blocks} />
      </div>
    </>
  );
};

export default FoundationDocument;
